import {defineStore} from 'pinia'
import {useTargetedMessagesService} from '@/composables/targeted_messages_service'

interface TargetedMessage {
  id: number
  CompanyNo: string
  End: string
  Persistent: boolean
  SalesArea: string
  Start: string
  Text: string
  Topic: string
  UserType: string
  locale: string
  localizations?: Array<{
    id: number
    locale: string
  }>
}

export const useTargetedMessagesStore = defineStore('TargetedMessagesModule', () => {
  const {getTargetedMessages} = useTargetedMessagesService()

  const messages = ref<TargetedMessage[]>([])
  const dismissedMessageIds = ref<(string | number)[]>([])
  const temporarilyDismissedIds = ref<(string | number)[]>([])

  const visibleMessages = computed(() => {
    return messages.value.filter(message => {
      if (message.Persistent) {
        return !temporarilyDismissedIds.value.includes(message.id)
      }
      return !dismissedMessageIds.value.includes(message.id)
    })
  })

  const fetchMessages = async (params?: any) => {
    const result = await getTargetedMessages(params)
    messages.value = Array.isArray(result)
      ? result.filter(message => message && typeof message.Text === 'string' && message.Text.trim() !== '')
      : []
  }

  const dismissMessage = (messageId: string | number) => {
    const message = messages.value.find(m => m.id === messageId)
    if (!message) {
      return
    }

    const allRelatedIds = [messageId]
    if (message.localizations) {
      allRelatedIds.push(...message.localizations.map(loc => loc.id))
    }

    if (message.Persistent) {
      allRelatedIds.forEach(id => {
        if (!temporarilyDismissedIds.value.includes(id)) {
          temporarilyDismissedIds.value.push(id)
        }
      })
    } else {
      allRelatedIds.forEach(id => {
        if (!dismissedMessageIds.value.includes(id)) {
          dismissedMessageIds.value.push(id)
        }
      })
    }
  }

  const clearMessages = () => {
    messages.value = []
    dismissedMessageIds.value = []
    temporarilyDismissedIds.value = []
  }

  const resetDismissedMessages = () => {
    dismissedMessageIds.value = []
    temporarilyDismissedIds.value = []
  }

  const resetTemporaryDismissals = () => {
    temporarilyDismissedIds.value = []
  }

  return {
    // State
    messages,
    dismissedMessageIds,
    temporarilyDismissedIds,
    // Getters
    visibleMessages,
    // Actions
    fetchMessages,
    dismissMessage,
    clearMessages,
    resetDismissedMessages,
    resetTemporaryDismissals
  }
}, {
  persist: {
    storage: persistedState.sessionStorage,
    paths: [
      'dismissedMessageIds',
      'temporarilyDismissedIds'
    ]
  }
})
